<script lang="ts">
	// Ask for a link that sets a new password (D19, UC-26).
	//
	// The server answers the same whether or not the address has an account,
	// so "Check your email" is all this screen ever says, and it says "if".
	// An owner who signs in with Google gains a password the same way.

	import Band from '../components/Band.svelte';
	import BrandMark from '../components/BrandMark.svelte';
	import Button from '../components/Button.svelte';
	import Field from '../components/Field.svelte';
	import Segmented from '../components/Segmented.svelte';
	import { message, type Language } from '../i18n/messages';
	import { forgotPassword, takeCarriedEmail } from '../owner/password';
	import { navigate } from '../router.svelte';

	interface Props {
		language: Language;
	}

	let { language }: Props = $props();

	// The address typed on Sign in, if any.
	let email = $state(takeCarriedEmail());
	let picked = $state<Language | null>(null);
	const mailLanguage = $derived<Language>(picked ?? language);

	let working = $state(false);
	let emailError = $state('');
	let failure = $state('');
	let sentTo = $state<string | null>(null);

	const languageOptions = $derived([
		{
			value: 'ar',
			label: message(language, 'settingsLanguageArabic'),
			testId: 'forgot-language-ar',
		},
		{
			value: 'msa',
			label: message(language, 'settingsLanguageMSA'),
			testId: 'forgot-language-msa',
		},
		{
			value: 'en',
			label: message(language, 'settingsLanguageEnglish'),
			testId: 'forgot-language-en',
		},
	]);

	async function send() {
		if (!email.trim() || working) return;
		emailError = '';
		failure = '';
		working = true;
		const result = await forgotPassword(email, mailLanguage);
		working = false;
		if (result.kind === 'sent') sentTo = result.email;
		else if (result.kind === 'invalid') emailError = message(language, 'createAccountBadEmail');
		else failure = message(language, 'loadFailed');
	}
</script>

<section class="screen" data-testid="screen-forgot">
	<div class="card">
		<BrandMark size={26} />
		{#if sentTo === null}
			<div class="words">
				<h1>{message(language, 'forgotTitle')}</h1>
				<p class="instruction">{message(language, 'forgotInstruction')}</p>
			</div>
			{#if failure}
				<Band tone="flag" testId="forgot-failure">{failure}</Band>
			{/if}
			<Field
				testId="forgot-email"
				label={message(language, 'signInEmail')}
				type="email"
				autocomplete="email"
				mono
				maxLength={254}
				value={email}
				error={emailError}
				errorTestId="forgot-email-error"
				onInput={(value) => (email = value)}
			/>
			<div class="pick">
				<span class="pick-label">{message(language, 'createAccountLanguage')}</span>
				<Segmented
					testId="forgot-language"
					label={message(language, 'createAccountLanguage')}
					options={languageOptions}
					value={mailLanguage}
					onChange={(value) => (picked = value as Language)}
				/>
			</div>
			<Button
				testId="forgot-submit"
				label={message(language, 'forgotSubmit')}
				wide
				disabled={!email.trim()}
				state={working ? 'pending' : 'idle'}
				pendingLabel={message(language, 'sending')}
				onPress={() => void send()}
			/>
		{:else}
			<div class="words" data-testid="forgot-sent">
				<h1>{message(language, 'checkEmailTitle')}</h1>
			</div>
			<p class="instruction">
				{message(language, 'forgotSentBefore')}
				<bdi class="address" dir="ltr" data-testid="forgot-sent-to">{sentTo}</bdi>{message(
					language,
					'forgotSentAfter',
				)}
			</p>
		{/if}
		<div class="foot">
			<Button
				testId="forgot-back"
				label={message(language, 'backToSignIn')}
				tone="text-quiet"
				size="sm"
				onPress={() => navigate('/owner')}
			/>
		</div>
	</div>
</section>

<style>
	.screen {
		min-block-size: 100dvh;
		display: grid;
		place-items: center;
		padding: var(--space-9);
		background: var(--surface-page);
	}

	.card {
		inline-size: 100%;
		max-inline-size: 420px;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-panel);
		padding: var(--card-pad);
	}

	.words {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
		text-wrap: pretty;
	}

	.instruction {
		margin: 0;
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.address {
		font-family: var(--font-mono);
		font-weight: 600;
		color: var(--text-strong);
	}

	.pick {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.pick-label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.foot {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-2);
		padding-block-start: var(--space-5);
		border-block-start: var(--border-width) solid var(--border-inset);
	}
</style>
